/* eslint no-console: off */
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useCallback, useState } from 'react';
import { useRecoilState } from 'recoil';
import { FaSpinner } from 'react-icons/fa';
import styles from './RegisterForm.module.scss';
import { genders } from '../../../constants/vip29';
import Vip29ApiService from '../../../services/Vip29ApiService';
import InputBooking from '../InputBooking';
import { registerFormState } from '../../../store/atoms/registerFormState';
import Input from '../Input';
import * as tse from '../../../utils/transactionSymmetricEncryption';
import ToastMessage from '../Toast';

const RegisterForm = (props) => {
    const { liffProfile, isLineBrowser } = props;
    const [errorPhone, setErrorPhone] = useState({
        phone_number: false,
    });
    const [loading, setLoading] = useState(false);

    const [formStore, setFormStore] = useRecoilState(registerFormState);

    const BookingSchema = yup.object().shape({
        first_name: yup.string().required('Required First Name'),
        sur_name: yup.string().required('Required Surname'),
        email: yup.string().email().required('Required Email'),
        date_of_birth: yup.string().required('Required Date Of Birth'),
        password: yup.string().required('Required Password').min('8'),
        confirm_password: yup
            .string()
            .oneOf([yup.ref('password'), null], 'Passwords must match'),
    });
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm({
        defaultValues: {
            gender: 'male',
        },
        resolver: yupResolver(BookingSchema),
    });

    const validatePhoneValue = (val) => {
        const { phone_number } = val;
        const PHONE_MIN_LENGTH = 10;
        const valid =
            phone_number === null
                ? true
                : phone_number.length < PHONE_MIN_LENGTH;
        setErrorPhone((d) => ({
            phone_number: valid,
        }));
    };

    const notify = useCallback((type, message) => {
        ToastMessage({ type, message });
        return null;
    }, []);

    const submitLogin = async (formData) => {
        try {
            // Verify code
            const { code } = await Vip29ApiService.verifyCode({
                email: formData.email,
            });

            // ENCRYPTION
            let formEnc = {
                ...formData,
                code,
            };
            if (isLineBrowser) {
                formEnc = {
                    ...formEnc,
                    line_id: liffProfile?.userId || null,
                };
            }
            const enc_data = tse.encrypt(formEnc, process.env.SDP_SALT);

            // Authentication
            const data = await Vip29ApiService.loginWithSetAccessTokenOnly({
                enc_data,
            });

            notify('success', 'Login Success');
            props.callbackToken(data);
        } catch (error) {
            notify('error', error?.message || 'Error');
            setLoading(false);
        }
    };

    const onSubmit = async (data) => {
        setLoading(true);
        if (errorPhone.phone_number) {
            return;
        }
        let line_id = null;
        if (isLineBrowser) {
            line_id = liffProfile?.userId || null;
        }
        const formData = { ...formStore, line_id };
        try {
            await Vip29ApiService.registerMember(formData);
            notify('success', 'Begin Login');
            await submitLogin(formData);
        } catch (e) {
            console.log(e);
            notify('error', e?.message || 'Error');
            setLoading(false);
        }
    };

    const onChange = (name, value) => {
        if (['phone_number'].includes(name)) {
            setFormStore({
                ...formStore,
                [name]: value.val,
                phone_number_format: `+${value.val}`,
                phone_dial_code: value.data.dialCode,
                phone_country_code: value.data.countryCode,
            });
            validatePhoneValue({ ...formStore, [name]: value.val });
        } else {
            setFormStore({
                ...formStore,
                [name]: value,
            });
        }
    };

    const validateForm = () => {
        validatePhoneValue(formStore);
    };

    return (
        <div className={styles.wrapper}>
            <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">
                <div className={styles.formGroup}>
                    <InputBooking
                        required={true}
                        label="First Name"
                        errors={errors}
                        register={register('first_name', {
                            onChange: (e) => {
                                onChange(e.target.name, e.target.value);
                            },
                        })}
                    />
                </div>
                <div className={styles.formGroup}>
                    <InputBooking
                        required={true}
                        label="Surname"
                        errors={errors}
                        register={register('sur_name', {
                            onChange: (e) => {
                                onChange(e.target.name, e.target.value);
                            },
                        })}
                    />
                </div>
                <div className={styles.formGroup}>
                    <InputBooking
                        type="datepicker"
                        required={true}
                        label="Date Of Birth"
                        errors={errors}
                        onChange={onChange}
                        register={register('date_of_birth')}
                    />
                </div>
                <div className={styles.formGroup}>
                    <InputBooking
                        type="phone"
                        required={false}
                        label="Phone Number"
                        errorPhone={errorPhone}
                        onChange={onChange}
                        register={register('phone_number')}
                    />
                </div>
                <div className={styles.formGroup}>
                    <Input
                        type="radio-register"
                        required={true}
                        label="Gender"
                        errors={errors}
                        register={register('gender', {
                            onChange: (e) => {
                                onChange(e.target.name, e.target.value);
                            },
                        })}
                        items={genders}
                    />
                </div>
                <div className={styles.formGroup}>
                    <InputBooking
                        required={true}
                        label="Email"
                        errors={errors}
                        register={register('email', {
                            onChange: (e) => {
                                onChange(e.target.name, e.target.value);
                            },
                        })}
                    />
                </div>
                <>
                    <div className={styles.formGroup}>
                        <InputBooking
                            type="password"
                            required={true}
                            label="Password"
                            errors={errors}
                            register={register('password', {
                                onChange: (e) => {
                                    onChange(e.target.name, e.target.value);
                                },
                            })}
                        />
                    </div>
                    <div className={styles.formGroup}>
                        <InputBooking
                            type="password"
                            required={true}
                            label="Confirm Password"
                            errors={errors}
                            register={register('confirm_password', {
                                onChange: (e) => {
                                    onChange(e.target.name, e.target.value);
                                },
                            })}
                        />
                    </div>
                </>

                <div className={styles.btnWrapper}>
                    <button
                        className={`${styles.bookingBtn} ${
                            loading ? styles.btnDisable : ''
                        }`}
                        type="submit"
                    >
                        <span>
                            {loading ? 'Just a moment' : 'SIGN UP'}
                            {loading && (
                                <FaSpinner className="loading-icon-payment" />
                            )}
                        </span>
                    </button>
                </div>
            </form>
        </div>
    );
};

export default RegisterForm;
